import { REPORT_SECTIONS } from './report-sections.constants';

/**
 * Splits a v2.1 learner_report_md into the sections the report renders as
 * cards. Headings are matched by their text, not by their '#' depth, so the
 * result survives the richtext editor shifting heading levels on a round trip.
 *
 * Returns null when none of the v2.1 top-level section headings are present
 * (every pre-v2.1 report), so the caller can keep the legacy single-blob render.
 */

export interface ParsedStep {
  number: number | null;
  title: string;
  /** Text between the step heading and its first subsection, if any. */
  intro?: string;
  whatHappened?: string;
  didWell?: string;
  canImprove?: string;
}

export interface ExtraSection {
  title: string;
  body: string;
}

export interface ParsedLearnerReport {
  /** Text before the first heading, if any. */
  preamble?: string;
  interactionSummary?: string;
  interactionStrengths?: string;
  developmentAreas?: string;
  /** Text directly under the Step-Specific Feedback heading, if any. */
  stepsIntro?: string;
  steps: ParsedStep[];
  /** Headings the layout does not know about; rendered after the steps so nothing is lost. */
  extraSections: ExtraSection[];
}

interface Block {
  title: string;
  lines: string[];
}

const HEADING_RE = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/;
// "Step 1 — Name", "Step 1 - Name", "Step 1: Name", "Step 1"
const STEP_RE = /^step\s+(\d+)\s*(?:[—–:\-]\s*)?(.*)$/i;

const norm = (s: string): string =>
  s.replace(/[*_`]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

const TOP = {
  summary: norm(REPORT_SECTIONS.INTERACTION_SUMMARY),
  strengths: norm(REPORT_SECTIONS.INTERACTION_STRENGTHS),
  development: norm(REPORT_SECTIONS.DEVELOPMENT_AREAS),
  steps: norm(REPORT_SECTIONS.STEP_SPECIFIC_FEEDBACK),
};

const SUB = {
  whatHappened: norm(REPORT_SECTIONS.WHAT_HAPPENED),
  didWell: norm(REPORT_SECTIONS.WHAT_YOU_DID_WELL),
  canImprove: norm(REPORT_SECTIONS.WHAT_YOU_CAN_IMPROVE),
};

const joinBody = (lines: string[]): string => lines.join('\n').trim();

function splitBlocks(md: string): { preamble: string[]; blocks: Block[] } {
  const preamble: string[] = [];
  const blocks: Block[] = [];
  let current: Block | null = null;
  let inFence = false;

  for (const raw of md.split(/\r?\n/)) {
    if (/^\s*```/.test(raw)) {
      inFence = !inFence;
    }
    const match = inFence ? null : HEADING_RE.exec(raw);
    if (match) {
      current = { title: match[1].trim(), lines: [] };
      blocks.push(current);
      continue;
    }
    (current ? current.lines : preamble).push(raw);
  }
  return { preamble, blocks };
}

/** True when the markdown carries at least one v2.1 top-level section heading. */
export function isV21LearnerReport(md: string | null | undefined): boolean {
  if (!md) { return false; }
  const known = [TOP.summary, TOP.strengths, TOP.development, TOP.steps];
  return splitBlocks(md).blocks.some(b => known.indexOf(norm(b.title)) !== -1);
}

export function parseLearnerReport(md: string | null | undefined): ParsedLearnerReport | null {
  if (!md || !isV21LearnerReport(md)) { return null; }

  const { preamble, blocks } = splitBlocks(md);
  const out: ParsedLearnerReport = { steps: [], extraSections: [] };

  const pre = joinBody(preamble);
  if (pre) { out.preamble = pre; }

  let inSteps = false;
  let step: ParsedStep | null = null;

  for (const block of blocks) {
    const key = norm(block.title);
    const text = joinBody(block.lines);

    if (key === TOP.summary) { out.interactionSummary = text; inSteps = false; step = null; continue; }
    if (key === TOP.strengths) { out.interactionStrengths = text; inSteps = false; step = null; continue; }
    if (key === TOP.development) { out.developmentAreas = text; inSteps = false; step = null; continue; }
    if (key === TOP.steps) {
      inSteps = true;
      step = null;
      if (text) { out.stepsIntro = text; }
      continue;
    }

    // A step heading counts even without the parent section heading, in case a
    // coach edit removed it.
    const stepMatch = STEP_RE.exec(block.title.replace(/[*_`]/g, '').trim());
    if (stepMatch) {
      inSteps = true;
      step = { number: parseInt(stepMatch[1], 10), title: stepMatch[2].trim() };
      if (text) { step.intro = text; }
      out.steps.push(step);
      continue;
    }

    if (inSteps && step) {
      if (key === SUB.whatHappened) { step.whatHappened = text; continue; }
      if (key === SUB.didWell) { step.didWell = text; continue; }
      if (key === SUB.canImprove) { step.canImprove = text; continue; }
    }

    out.extraSections.push({ title: block.title, body: text });
  }

  return out;
}

// ---- Serializer (inverse of parseLearnerReport) ----

const H2 = (title: string): string => `## ${title}`;
const H3 = (title: string): string => `### ${title}`;
const H4 = (title: string): string => `#### ${title}`;

const hasText = (s: string | null | undefined): s is string => !!s && s.trim().length > 0;

/**
 * Neutralises heading-looking lines inside a section body (outside code fences)
 * so text a coach pastes cannot split a card on the next parse. Showdown renders
 * the escaped `\#` as a literal `#`.
 */
function escapeBodyHeadings(body: string): string {
  let inFence = false;
  return body
    .replace(/\r/g, '')
    .split('\n')
    .map(line => {
      if (/^\s*```/.test(line)) { inFence = !inFence; return line; }
      return inFence ? line : line.replace(/^(\s{0,3})(#{1,6})(\s)/, '$1\\$2$3');
    })
    .join('\n');
}

function stepHeading(step: ParsedStep): string {
  const title = (step.title || '').trim();
  if (step.number === null) { return title; }
  return title ? `Step ${step.number} — ${title}` : `Step ${step.number}`;
}

/**
 * Reassembles a ParsedLearnerReport into canonical v2.1 markdown: the fixed H2
 * sections in contract order, `### Step N — <name>` blocks with their H4
 * subsections, then any extra sections as H2. Optional text is emitted only
 * when non-empty (empty subsections are omitted by contract), and
 * `## Step-Specific Feedback` is emitted only when there is step content.
 *
 * Round trip: parseLearnerReport(serializeLearnerReport(parseLearnerReport(md)))
 * equals parseLearnerReport(md) once empty-string optionals are dropped, and
 * serializeLearnerReport is idempotent.
 */
export function serializeLearnerReport(p: ParsedLearnerReport): string {
  const out: string[] = [];
  const push = (heading: string | null, body?: string | null): void => {
    if (heading) { out.push(heading, ''); }
    if (hasText(body)) { out.push(escapeBodyHeadings(body.trim()), ''); }
  };

  if (hasText(p.preamble)) { push(null, p.preamble); }
  if (hasText(p.interactionSummary)) { push(H2(REPORT_SECTIONS.INTERACTION_SUMMARY), p.interactionSummary); }
  if (hasText(p.interactionStrengths)) { push(H2(REPORT_SECTIONS.INTERACTION_STRENGTHS), p.interactionStrengths); }
  if (hasText(p.developmentAreas)) { push(H2(REPORT_SECTIONS.DEVELOPMENT_AREAS), p.developmentAreas); }

  const steps = p.steps || [];
  if (steps.length || hasText(p.stepsIntro)) {
    push(H2(REPORT_SECTIONS.STEP_SPECIFIC_FEEDBACK), p.stepsIntro);
    for (const step of steps) {
      push(H3(stepHeading(step)), step.intro);
      if (hasText(step.whatHappened)) { push(H4(REPORT_SECTIONS.WHAT_HAPPENED), step.whatHappened); }
      if (hasText(step.didWell)) { push(H4(REPORT_SECTIONS.WHAT_YOU_DID_WELL), step.didWell); }
      if (hasText(step.canImprove)) { push(H4(REPORT_SECTIONS.WHAT_YOU_CAN_IMPROVE), step.canImprove); }
    }
  }

  for (const extra of p.extraSections || []) {
    push(H2(extra.title.trim()), extra.body);
  }

  return out.join('\n').trim() + '\n';
}

// ---- Coach Summary (coach_addendum_md) ----

export type CoachSectionKind = 'concerns' | 'wins' | 'growth' | 'debrief' | 'other';

export interface CoachSection {
  title: string;
  kind: CoachSectionKind;
  body: string;
}

export interface ParsedCoachSummary {
  /** Text before the first heading, if any. */
  preamble?: string;
  /** In source order; unknown headings are kept as kind 'other'. */
  sections: CoachSection[];
}

const COACH: { [key: string]: CoachSectionKind } = {
  [norm(REPORT_SECTIONS.COACH_CONCERNS)]: 'concerns',
  [norm(REPORT_SECTIONS.COACH_WINS)]: 'wins',
  [norm(REPORT_SECTIONS.COACH_GROWTH)]: 'growth',
  [norm(REPORT_SECTIONS.COACH_DEBRIEF)]: 'debrief',
};

/**
 * Splits coach_addendum_md into its fixed sections (prompt rule C3) so the
 * Coach Summary can render them as cards. Returns null when none of the known
 * headings are present, so the caller keeps the single-blob render.
 */
export function parseCoachSummary(md: string | null | undefined): ParsedCoachSummary | null {
  if (!md) { return null; }

  const { preamble, blocks } = splitBlocks(md);
  if (!blocks.some(b => !!COACH[norm(b.title)])) { return null; }

  const out: ParsedCoachSummary = { sections: [] };
  const pre = joinBody(preamble);
  if (pre) { out.preamble = pre; }

  for (const block of blocks) {
    out.sections.push({
      title: block.title.replace(/[*_`]/g, '').trim(),
      kind: COACH[norm(block.title)] || 'other',
      body: joinBody(block.lines),
    });
  }
  return out;
}

// Boilerplate openers of the coaching lines (prompt rule C6). The card already
// says whether it is a win or a growth item, so the UI drops them.
const COACH_TIP_OPENERS = [
  /^encourage by mentioning this in debrief by name\s*[—–-]\s*/i,
  /^coach this by gently asking\s+/i,
];

/** Drops a coaching line's fixed opener and capitalises what is left. */
export function trimCoachTip(tip: string): string {
  let out = tip;
  for (const re of COACH_TIP_OPENERS) {
    out = out.replace(re, '');
  }
  if (out === tip) { return tip; }
  // Capitalise the first letter, skipping any opening quote marks.
  return out.replace(/^([\s"'“‘]*)([a-z])/, (_m, lead: string, ch: string) => lead + ch.toUpperCase());
}

// ---- Display helper: fold an item's evidence quote into the item ----

const LIST_ITEM_RE = /^\s{0,3}[-*+]\s+\S/;
const QUOTE_LINE_RE = /^\s*>\s?(.*)$/;

/**
 * Improvement items carry their evidence quote on a line of its own under the
 * bullet (`  > “quote” (MM:SS)`). Markdown can't nest that quote in the list
 * item, so it would render as a separate block after the list. For display,
 * append the quote to the end of its item instead, so it reads as part of the
 * item and wraps with it. A quote that doesn't follow an item is left alone.
 */
export function inlineItemQuotes(md: string): string {
  if (!md || !/^\s*>/m.test(md)) { return md; }
  const out: string[] = [];
  let lastItemLine = -1;  // index in out of the current item's last text line
  for (const line of md.replace(/\r/g, '').split('\n')) {
    const quote = QUOTE_LINE_RE.exec(line);
    if (quote && lastItemLine >= 0) {
      if (quote[1].trim()) {
        out.length = lastItemLine + 1;  // drop blank lines between item and quote
        out[lastItemLine] = out[lastItemLine].replace(/\s+$/, '') + ' ' + quote[1].trim();
      }
      continue;
    }
    if (!line.trim()) {
      out.push(line);
      continue;
    }
    if (LIST_ITEM_RE.test(line)) {
      lastItemLine = out.length;
    } else if (lastItemLine >= 0 && (out.length - 1 === lastItemLine || /^\s+\S/.test(line))) {
      // Continuation of the item (wrapped or indented text).
      lastItemLine = out.length;
    } else {
      lastItemLine = -1;
    }
    out.push(line);
  }
  return out.join('\n');
}
